import { createTheme, alpha, Theme } from '@mui/material';
import { PureLightTheme } from './PureLightTheme';
import { DarkTheme } from './DarkTheme';
import { ModernPalette, paletteById, DEFAULT_PALETTE_ID } from '../modernPalettes';

// =====================================================================================
// "Full Modern" application theme factory — company opt-in via custom settings
// AppTheme='modern', AppThemeColor=<palette id>, AppThemeSurface='light'|'gradient'.
//
// Built as a deep-merge OVER the classic schemes (createTheme(base, overrides)), so every
// component override and custom field Classic defines stays intact unless restyled here.
// Because the whole app is MUI, this restyles every module and form: gradient canvas,
// glass cards, gradient buttons, gradient sidebar, rounded inputs.
//
// Components branch on `(theme as any).isModern` for launcher-style flourishes and read
// `(theme as any).modernAccentGradient` so they follow the company's chosen palette.
//
// CONSTRAINT: theme.header.background MUST stay a plain colour string — the Header wraps
// it in alpha(), which throws on gradients. Sidebar background IS applied as a CSS
// `background`, so a gradient is safe there.
// =====================================================================================

const glassLight = (p: ModernPalette) => ({
  borderRadius: 16,
  background: 'rgba(255,255,255,0.78)',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(p.primary, 0.12)}`,
  boxShadow: `0 10px 30px -12px ${alpha(p.primaryDark, 0.28)}`
});

const glassDark = (p: ModernPalette) => ({
  borderRadius: 16,
  background: 'rgba(17,20,42,0.55)',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(p.primaryLight, 0.16)}`,
  boxShadow: '0 10px 30px -12px rgba(0,0,0,0.55)'
});

const scrollbar = (thumb: string, hover: string) => ({
  '*::-webkit-scrollbar': { width: 8, height: 8 },
  '*::-webkit-scrollbar-track': { background: 'transparent' },
  '*::-webkit-scrollbar-thumb': { background: thumb, borderRadius: 8 },
  '*::-webkit-scrollbar-thumb:hover': { background: hover }
});

/**
 * Build a modern theme for a palette.
 * @param dark true for the dark / "Full Gradient" immersive canvas.
 */
export function createModernTheme(paletteOrId: ModernPalette | string, dark = false): Theme {
  const p = typeof paletteOrId === 'string' ? paletteById(paletteOrId) : paletteOrId;
  const accent = `linear-gradient(135deg, ${p.primary} 0%, ${p.second} 100%)`;
  const accentSoft = `linear-gradient(135deg, ${p.primaryLight} 0%, ${p.primary} 100%)`;
  // "Paper" identities (off-white, smoke, platinum…) stay LIGHT even in immersive
  // mode — a dark canvas would defeat the point — so they always build on the light
  // scheme and put DARK text on the transparent chrome.
  const paper = p.isLight === true;
  const onDark = dark && !paper;
  const base = onDark ? (DarkTheme as any) : (PureLightTheme as any);

  // IMMERSIVE (dark / "Full Gradient"): the page carries ONE gradient and the chrome
  // paints nothing — sidebar and header are fully transparent so the canvas runs edge
  // to edge behind them, exactly like the Modern Dashboard launcher.
  const immersive = dark;

  const custom = {
    isModern: true,
    modernPaletteId: p.id,
    modernSurface: immersive ? 'gradient' : 'light',
    modernImmersive: immersive,
    /** true when the chrome sits on a LIGHT canvas (paper palettes) — drives logo choice. */
    modernPaper: paper,
    modernAccentGradient: accent,
    modernAccentGradientDark: accentSoft,
    modernSidebarGradient: p.sidebar
  };

  return createTheme(base, {
    ...custom,
    colors: {
      primary: {
        lighter: alpha(onDark ? p.primaryLight : p.primary, 0.12),
        light: onDark ? '#ffffff' : p.primaryLight,
        main: onDark ? p.primaryLight : p.primary,
        dark: onDark ? p.primary : p.primaryDark
      },
      // Legacy consumers that paint their own body background blend with the canvas
      layout: { general: { bodyBg: 'transparent' } },
      shadows: {
        primary: `0px 1px 4px ${alpha(p.primary, 0.25)}, 0px 3px 12px 2px ${alpha(p.primary, 0.35)}`
      }
    },
    sidebar: {
      // Transparent in immersive mode — the page gradient IS the sidebar background.
      background: immersive ? 'transparent' : p.sidebar,
      textColor: paper ? '#1e293b' : '#e2e8f0',
      dividerBg: paper ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)',
      menuItemColor: paper ? 'rgba(30,41,59,0.78)' : 'rgba(226,232,240,0.82)',
      menuItemColorActive: '#ffffff', // active item sits on the accent gradient pill
      menuItemBg: 'transparent',
      menuItemBgActive: paper ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.12)',
      menuItemIconColor: paper ? 'rgba(30,41,59,0.65)' : 'rgba(226,232,240,0.72)',
      menuItemIconColorActive: '#ffffff',
      menuItemHeadingColor: paper ? 'rgba(30,41,59,0.45)' : 'rgba(226,232,240,0.5)',
      // Hover state differs per canvas: a light wash on paper, a light veil on dark.
      menuItemHoverBg: paper ? 'rgba(15,23,42,0.05)' : 'rgba(255,255,255,0.08)',
      menuItemHoverColor: paper ? p.primaryDark : '#ffffff',
      menuItemIconBg: paper ? 'rgba(15,23,42,0.05)' : 'rgba(255,255,255,0.08)',
      scrollbarColor: p.primary,
      boxShadow: immersive ? 'none' : '2px 0 18px rgba(2, 6, 23, .28)'
    },
    header: {
      // Immersive keeps this a PLAIN colour that the Header turns into frosted glass
      // (alpha + blur) — content must scroll BEHIND the header, not show through it.
      background: immersive ? (paper ? '#ffffff' : p.darkBase) : p.header,
      textColor: paper ? '#1e293b' : '#e2e8f0',
      boxShadow: immersive ? 'none' : `0px 2px 16px -4px ${alpha(p.primaryDark, 0.6)}`
    },
    palette: {
      primary: {
        light: onDark ? '#ffffff' : p.primaryLight,
        main: onDark ? p.primaryLight : p.primary,
        dark: onDark ? p.primary : p.primaryDark
      },
      background: {
        default: 'transparent',
        // Kept high-opacity: every floating surface (pickers, poppers, menus) falls back
        // to this. Cards get their translucent glass from the MuiCard override instead.
        paper: onDark ? 'rgba(20,23,45,0.94)' : 'rgba(255,255,255,0.96)'
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // The gradient lives on <html>, NOT <body>. index.html pins `html, body
          // { height: 100% }`, so the body box is only viewport-tall — a background on
          // body stops at that box and scrolling a long page revealed the flat base
          // colour underneath. The root element's background is propagated to the
          // canvas, so it covers the full scrollable page at any scroll position, and
          // `fixed` keeps it anchored to the viewport.
          html: {
            background: onDark ? p.darkCanvas : p.lightCanvas,
            backgroundAttachment: 'fixed',
            backgroundColor: onDark ? p.darkBase : p.lightBase,
            backgroundRepeat: 'no-repeat',
            minHeight: '100%'
          },
          body: {
            background: 'transparent',
            backgroundColor: 'transparent',
            minHeight: '100vh'
          },
          // NOTE: the logo is inverted where it sits on a dark surface (Sidebar logo
          // strip), NOT globally — a global invert would blank it on the login page.
          ...scrollbar(alpha(p.primary, onDark ? 0.45 : 0.35), p.primary)
        }
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, fontWeight: 600, textTransform: 'none' },
          containedPrimary: {
            background: accent,
            color: '#ffffff',
            boxShadow: `0 6px 16px -6px ${alpha(p.primary, 0.55)}`,
            '&:hover': {
              background: accentSoft,
              boxShadow: `0 10px 22px -8px ${alpha(p.primary, 0.65)}`
            }
          },
          outlinedPrimary: { borderColor: alpha(onDark ? p.primaryLight : p.primary, 0.4) }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            ...(onDark ? glassDark(p) : glassLight(p)),
            transition: 'transform .18s ease, box-shadow .18s ease',
            '&:hover': {
              boxShadow: onDark
                ? '0 16px 40px -14px rgba(0,0,0,0.65)'
                : `0 16px 40px -14px ${alpha(p.primaryDark, 0.35)}`
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 16 },
          elevation0: { background: 'transparent' },
          outlined: { borderColor: alpha(onDark ? p.primaryLight : p.primary, 0.16) }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
            background: onDark ? 'rgba(16,19,40,0.95)' : 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(14px)',
            boxShadow: `0 24px 70px -24px ${alpha(p.primaryDark, 0.55)}`
          }
        }
      },
      MuiDrawer: { styleOverrides: { paper: { border: 0 } } },
      // Floating surfaces (user menu, selects, autocompletes, date pickers) must be
      // NEAR-OPAQUE. The translucent background.paper used for in-page cards would let
      // the header and page bleed through a dropdown, which reads as the popup sitting
      // *behind* the header.
      ...(() => {
        const popoverPaper = {
          backgroundColor: onDark ? 'rgba(20,23,45,0.98)' : 'rgba(255,255,255,0.99)',
          backdropFilter: 'blur(18px) saturate(150%)',
          borderRadius: 14,
          border: `1px solid ${alpha(onDark ? p.primaryLight : p.primary, 0.16)}`,
          boxShadow: onDark
            ? '0 20px 50px -18px rgba(0,0,0,0.75)'
            : `0 20px 50px -18px ${alpha(p.primaryDark, 0.42)}`
        };
        return {
          MuiPopover: { styleOverrides: { paper: popoverPaper } },
          MuiMenu: { styleOverrides: { paper: popoverPaper } },
          MuiAutocomplete: { styleOverrides: { paper: popoverPaper } }
        };
      })(),
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            background: onDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(onDark ? p.primaryLight : p.primary, 0.45)
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: onDark ? p.primaryLight : p.primary,
              borderWidth: 2
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 9, fontWeight: 600 },
          colorPrimary: { background: accent, color: '#fff' }
        }
      },
      MuiAvatar: {
        styleOverrides: { colorDefault: { background: accent, color: '#fff' } }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              background: alpha(p.primary, onDark ? 0.16 : 0.06),
              color: onDark ? p.primaryLight : p.primaryDark,
              fontWeight: 700,
              borderBottom: `1px solid ${alpha(p.primary, 0.16)}`
            }
          }
        }
      },
      MuiTableRow: {
        styleOverrides: { root: { '&:hover': { background: alpha(p.primary, onDark ? 0.10 : 0.04) } } }
      },
      MuiTabs: {
        styleOverrides: { indicator: { background: accent, height: 3, borderRadius: 3 } }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: onDark ? p.primaryLight : p.primary }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 99, background: alpha(p.primary, 0.12) },
          bar: { background: accent, borderRadius: 99 }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            '&.Mui-selected': {
              background: alpha(p.primary, 0.16),
              color: onDark ? p.primaryLight : p.primaryDark
            }
          }
        }
      },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked': { color: p.primary } },
          track: { '.Mui-checked.Mui-checked + &': { backgroundColor: p.primary } }
        }
      }
    }
  } as any);
}

// Default (violet) instances kept for the static themeMap / any direct importers.
export const ModernTheme = createModernTheme(DEFAULT_PALETTE_ID, false);
export const ModernDarkTheme = createModernTheme(DEFAULT_PALETTE_ID, true);
